import Image, { ImageProps } from "next/image";
import { useEffect, useState } from "react";
import { Box, BoxProps, Stack, StackProps, Typography, TypographyProps, useMediaQuery } from "@mui/material";
import { API_URL } from "helpers/common";
import { PageProps } from "pages/_app";
import trustScoresIcon from "assets/icons/landing-page/trustScore.png";
import header1 from "assets/icons/landing-page/mtd-sorted-header.png";
import header2 from "assets/icons/landing-page/mtd-sorted-header2.png";
import googleIcon from "assets/icons/landing-page/google-review.png";
import xeroIcon from "assets/icons/landing-page/xero.png";
import freeagentIcon from "assets/icons/landing-page/FreeAgent_Blue.png";
import intuitIcon from "assets/icons/landing-page/intuit.svg";
import router from "next/router";
import AppLink from "components/v2/atoms/app-link/AppLink";
import AppIconSocialButton, {
    AppIconSocialButtonProps,
} from "components/v2/atoms/app-social-button/AppIconSocialButton";
import AppButton from "components/v2/atoms/app-button/AppButton";
import { AppSocialButtonProps } from "components/v2/atoms/app-social-button/AppSocialButton";
import emailWhiteIcon from "assets/icons/email-white.svg";
import DoneIcon from "@mui/icons-material/Done";
import saleBannerImage from "assets/icons/sale-banner-blue.svg";
import { usePartnerId } from "hooks/partner-id";

interface RightPanelProps {
    isMobile: boolean;
    adsum_fees: string;
    partner: string;
}

interface LeftPanelProps {
    isMobile: boolean;
    partner: string;
}

interface ContentProps {
    partner: string;
    fees: string;
}

function MtdSorted(_: PageProps) {
    const [fees] = useState<string>("15");
    const { partner } = usePartnerId({ defaultPartner: "mtdsorted" });

    useEffect(() => {
        if (router.isReady) {
            router.push(`/v2/instant-vat?partner_id=${partner}`);
        }
    }, [router.isReady]);

    const contentProps: ContentProps = {
        partner: partner,
        fees: fees,
    };

    return <Content {...contentProps} />;
}

function Content(props: ContentProps) {
    const isMobile = useMediaQuery("(max-width:1040px)");

    const outerContainerProps: BoxProps = {
        sx: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: isMobile ? 0 : "30px",
            boxSizing: "border-box",
            minHeight: isMobile ? "100%" : "80vh",
            height: "auto",
            maxHeight: "auto",
            padding: isMobile ? 2 : "auto",
            minWidth: isMobile ? "375px" : "1044px",
            maxWidth: isMobile ? "650px" : "1044px",
            margin: "auto",
        },
    };

    const stackProps: StackProps = {
        sx: {
            flexDirection: isMobile ? "column" : "row",
        },
        gap: 3,
    };

    const leftStackProps: StackProps = {
        sx: {
            backgroundColor: "#FFFFFF",
            width: "auto",
        },
        border: 1,
        position: "relative",
        borderColor: "#E3E6EB",
        borderRadius: 1,
        boxShadow: 1,
        maxWidth: isMobile ? "615px" : "560px",
    };

    const rightStackProps: StackProps = {
        sx: {
            backgroundColor: "#FFFFFF",
            width: "auto",
            marginBottom: "auto",
        },
        border: 1,
        borderColor: "#E3E6EB",
        borderRadius: 1,
        boxShadow: 1,
        maxWidth: isMobile ? "615px" : "450px",
    };

    const reviewStackProps: StackProps = {
        sx: {
            marginTop: { xs: 1, md: 4 },
            marginLeft: { xs: 2, md: 0 },
            marginRight: { xs: 2, md: 0 },
            marginBottom: { xs: 1, md: 4 },
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            justifyContent: "space-between",
        },
        gap: 4,
    };

    const subReviewStackProps: StackProps = {
        sx: {
            flexDirection: "row",
            gap: { xs: 4, md: 10 },
        },
    };

    const rightPanelProps: RightPanelProps = {
        isMobile: isMobile,
        adsum_fees: props.fees,
        partner: props.partner,
    };

    const leftPanelProps: LeftPanelProps = {
        isMobile: isMobile,
        partner: props.partner,
    };

    const headerStackProps: StackProps = {
        sx: {
            backgroundColor: "#13D6B0",
            alignItems: "center",
            cursor: "pointer",
        },
        border: 1,
        borderColor: "rgba(0, 0, 0, 0.15)",
        borderRadius: 1,
        boxShadow: 1,
        padding: 1,
        mb: 3,
        onClick: () => {
            let url = API_URL + "/portal/v2/instant-vat/steps/email";
            if (props.partner) {
                url += `?partner_id=${props.partner}`;
            } else {
                url += "?partner_id=mtdsorted";
            }
            location.href = url;
        },
    };

    const saleBannerImageProps: ImageProps = {
        src: saleBannerImage,
        alt: "Sale Banner",
        style: {
            maxWidth: isMobile ? "75px" : "150px",
            height: "auto",
            position: "absolute",
            top: isMobile ? -10 : -20,
            right: isMobile ? -8 : -10,
        },
    };

    return (
        <Box {...outerContainerProps}>
            <Stack>
                {props.fees === "0" ? (
                    <Stack {...headerStackProps}>
                        <Typography variant="subtitle2" color={"white"} fontWeight={500}>
                            MTDsorted exclusive: 30 days free trial
                        </Typography>
                        <Stack flexDirection={"row"} gap={1.5}>
                            <Box display="flex" alignItems="center">
                                <DoneIcon style={{ marginRight: "2", color: "white", height: "15", width: "15" }} />
                                <Typography variant="body2" color={"white"}>
                                    No payment
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <DoneIcon style={{ marginRight: "2", color: "white", height: "15", width: "15" }} />
                                <Typography variant="body2" color={"white"}>
                                    No obligation
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <DoneIcon style={{ marginRight: "2", color: "white", height: "15", width: "15" }} />
                                <Typography variant="body2" color={"white"}>
                                    Cancel anytime
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                ) : null}
                <Stack {...stackProps}>
                    <Stack {...leftStackProps}>
                        <LeftPanel {...leftPanelProps} />
                        <Image {...saleBannerImageProps} alt="Sale Banner" />
                    </Stack>
                    {isMobile && (
                        <Stack {...reviewStackProps}>
                            <Stack {...subReviewStackProps}>
                                <Image
                                    src={trustScoresIcon}
                                    alt="tpreview"
                                    width="118.25"
                                    height={"57"}
                                    style={{
                                        maxWidth: "100%",
                                        height: "auto",
                                    }}
                                />
                                <Image
                                    src={googleIcon}
                                    alt="Google"
                                    width="175"
                                    height={"59"}
                                    style={{
                                        maxWidth: "100%",
                                        height: "auto",
                                    }}
                                />
                            </Stack>
                            <Stack {...subReviewStackProps}>
                                <Image
                                    src={xeroIcon}
                                    alt="Xero"
                                    width="106"
                                    height={"56"}
                                    style={{
                                        maxWidth: "100%",
                                        height: "auto",
                                    }}
                                />
                                <Image
                                    src={intuitIcon}
                                    alt="Intuit"
                                    style={{
                                        maxWidth: "100%",
                                        height: "auto",
                                    }}
                                />
                            </Stack>
                            <Stack {...subReviewStackProps}>
                                <Image
                                    src={freeagentIcon}
                                    alt="Freeagent"
                                    width="150"
                                    height={"36.2"}
                                    style={{
                                        maxWidth: "100%",
                                        height: "auto",
                                    }}
                                />
                            </Stack>
                        </Stack>
                    )}
                    <Stack {...rightStackProps}>
                        <RightPanel {...rightPanelProps} />
                    </Stack>
                </Stack>
                {!isMobile && (
                    <Stack {...reviewStackProps}>
                        <Stack {...subReviewStackProps}>
                            <Image
                                src={trustScoresIcon}
                                alt="tpreview"
                                width="118.25"
                                height={"57"}
                                style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                }}
                            />
                            <Image
                                src={googleIcon}
                                alt="Google"
                                width="175"
                                height={"59"}
                                style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                }}
                            />
                        </Stack>
                        <Stack {...subReviewStackProps}>
                            <Image
                                src={xeroIcon}
                                alt="Xero"
                                width="106"
                                height={"56"}
                                style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                }}
                            />
                            <Image
                                src={intuitIcon}
                                alt="Intuit"
                                style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                }}
                            />
                        </Stack>
                        <Stack {...subReviewStackProps}>
                            <Image
                                src={freeagentIcon}
                                alt="Freeagent"
                                width="150"
                                height={"36.2"}
                                style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                }}
                            />
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}

export default MtdSorted;

function LeftPanel(props: LeftPanelProps) {
    const stackProps: StackProps = {
        sx: {},
    };

    const bodyStackProps: StackProps = {
        sx: {
            p: { xs: 3 },
            alignContent: { xs: "center", md: "flex-start" },
            justifyContent: { xs: "center", md: "flex-start" },
            textAlign: "left",
        },
        gap: 2,
    };

    const loginLinkProps: TypographyProps = {
        variant: "body2",
        color: "text.disabled",
        sx: {
            textAlign: { xs: "center", md: "left" },
        },
    };

    const buttonStackProps: StackProps = {
        sx: {
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            mt: 1,
            mb: 1,
        },
        gap: { xs: 1, md: 2 },
    };

    const googleButtonProps: AppIconSocialButtonProps = {
        id: "instant-vat_steps_email_google",
        brand: "google",
        onClick() {
            let url = API_URL + "/google_signup";
            if (props.partner) {
                url += `?partner_signup=${props.partner}`;
            } else {
                url += "?partner_signup=mtdsorted";
            }

            location.href = url;
        },
    };

    const microsoftButtonProps: AppIconSocialButtonProps = {
        id: "instant-vat_steps_email_microsoft",
        brand: "microsoft",
        onClick() {
            let url = API_URL + "/microsoft_signup";
            if (props.partner) {
                url += `?partner_signup=${props.partner}`;
            } else {
                url += "?partner_signup=mtdsorted";
            }

            location.href = url;
        },
    };

    const hmrcButtonProps: AppIconSocialButtonProps = {
        id: "instant-vat_steps_hmrc",
        brand: "hmrc",
        onClick: () => {
            let url = API_URL + "/portal/v2/instant-vat/steps/government-gateway";
            if (props.partner) {
                url += `?partner_id=${props.partner}`;
            } else {
                url += "?partner_id=mtdsorted";
            }
            location.href = url;
        },
    };

    const footerProps: TypographyProps = {
        sx: {
            mt: { xs: -2, md: "auto" },
            p: 2,
            textAlign: { xs: "center", md: "left" },
        },
        color: "#9DA4AF",
        fontSize: { xs: 8, md: 10 },
    };

    const brandImage = (
        <Image
            src={emailWhiteIcon}
            alt={emailWhiteIcon}
            width={20}
            height={20}
            priority
            style={{
                maxWidth: "100%",
                height: "auto",
            }}
        />
    );
    const emailButtonProps: AppSocialButtonProps = {
        id: "instant-vat_steps_email_email",
        brand: "email",
        variant: "contained",
        startIcon: brandImage,
        sx: {
            color: "white",
            backgroundColor: "#2A93D5",
            fontWeight: 520,
            "&:hover": {
                backgroundColor: "#2A93D5",
                borderColor: "#2A93D5",
            },
            width: { xs: "100%", md: "auto" },
            gap: 0.5,
            justifyContent: "center",
            paddingRight: { xs: 2, md: 5 },
            paddingLeft: { xs: 2, md: 4 },
        },
        onClick() {
            let url = API_URL + "/portal/v2/instant-vat/steps/email";
            if (props.partner) {
                url += `?partner_id=${props.partner}`;
            } else {
                url += "?partner_id=mtdsorted";
            }
            location.href = url;
        },
        children: "Continue with email",
    };

    return (
        <Stack {...stackProps}>
            <Box
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    position: "relative",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                <Image
                    src={header1}
                    alt="header1"
                    style={{
                        objectFit: "cover",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        maxWidth: "100%",
                        height: "auto",
                    }}
                />
            </Box>
            <Stack {...bodyStackProps}>
                <Typography variant="body1" mb={-1}>
                    Submit your VAT return as normal
                </Typography>
                <Typography variant="h1" color="#192851">
                    Your VAT refund paid
                    {props.isMobile ? " " : <br />}
                    in as little as 24 hours.
                </Typography>
                <Typography color="#9DA4AF" variant="body2">
                    On average, our customers save 47 days every year.
                </Typography>
                <Typography variant="body2" fontWeight={500} mt={1.5}>
                    Set up in under 4 minutes.
                </Typography>
                <Stack {...buttonStackProps}>
                    <AppButton {...emailButtonProps} />
                    <Typography variant="body2">or</Typography>
                    <Stack flexDirection={"row"} gap={2}>
                        <AppIconSocialButton {...googleButtonProps} />
                        <AppIconSocialButton {...microsoftButtonProps} />
                        <AppIconSocialButton {...hmrcButtonProps} />
                    </Stack>
                </Stack>
                <Typography {...loginLinkProps}>
                    Already have an account?{" "}
                    <AppLink color={"#2A93D5"} id="login-link" href={API_URL + "/portal/v2/auth/login"}>
                        Log in
                    </AppLink>
                </Typography>
            </Stack>
            <Typography {...footerProps}>
                As part of our partnership we&apos;ll keep MTDsorted updated if you move forward with InstantVAT
                <sup>®</sup>.
            </Typography>
        </Stack>
    );
}

function RightPanel(props: RightPanelProps) {
    const stackProps: StackProps = {
        sx: {},
    };

    const bodyStackProps: StackProps = {
        sx: {
            paddingTop: { xs: -1, md: 1 },
            paddingBottom: { xs: 2, md: 4 },
            paddingLeft: { xs: 1, md: 2 },
            paddingRight: { xs: 1, md: 2 },
        },
        gap: 2,
    };

    return (
        <Stack {...stackProps}>
            <Box
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    position: "relative",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                <Image
                    src={header2}
                    alt="header2"
                    onClick={() => {
                        router.push("/mtdsorted/help?partner=" + props.partner);
                    }}
                    style={{
                        objectFit: "cover",
                        cursor: "pointer",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        maxWidth: "100%",
                        height: "auto",
                    }}
                />
            </Box>
            <Stack {...bodyStackProps}>
                <Typography variant="body2" lineHeight={3}>
                    <ul className="accelerated-dialog-list">
                        <li>
                            <Typography variant="body2" style={{ color: "#9DA4AF" }}>
                                <span style={{ fontWeight: 500, color: "#32373E" }}>47 days saved per year</span> - On
                                average, our customers save 47 days waiting for HMRC every year.
                            </Typography>
                        </li>
                        <li>
                            {props.adsum_fees === "0" ? (
                                <Typography variant="body2" style={{ color: "#9DA4AF" }}>
                                    <span
                                        style={{
                                            fontWeight: 500,
                                            color: "#32373E",
                                        }}>
                                        30 days completely free
                                    </span>{" "}
                                    then just 2% per refund, no commitment or subscription required.
                                </Typography>
                            ) : (
                                <Typography variant="body2" style={{ color: "#9DA4AF" }}>
                                    <span style={{ fontWeight: 500, color: "#32373E" }}>Business as usual</span> - No
                                    paperwork, no process changes. Submit your return as usual, just get paid faster.
                                </Typography>
                            )}
                        </li>
                        <li>
                            <Typography variant="body2" style={{ color: "#9DA4AF" }}>
                                <span style={{ fontWeight: 500, color: "#32373E" }}>From just £5 per month</span> +0.99%
                                added to amounts over £1,000.
                            </Typography>
                        </li>
                    </ul>
                </Typography>
            </Stack>
        </Stack>
    );
}
